// src/features/idealWeek/hooks/useIdealWeek.ts
// Ideal Week 資料載入、快取與更新的自訂 Hook

import { useState, useEffect, useCallback } from 'react';
import { IdealWeek, IdealWeekEntry } from '../types/idealWeek';
import { updateUserIdealWeek } from '../services/idealWeekService';
import { getUserData } from '../../../services/auth';

// TODO: migration shim — remove after 2026-07-01
// 舊格式 idealWeek 值為純 string，新格式為 { title, description }
function normalizeIdealWeekEntry(value: unknown): IdealWeekEntry {
  if (typeof value === 'string') {
    return { title: value };
  }
  return value as IdealWeekEntry;
}

function normalizeIdealWeek(raw: unknown): IdealWeek {
  if (!raw || typeof raw !== 'object') return {};
  const result: IdealWeek = {};
  for (const key of [0, 1, 2, 3, 4, 5, 6] as const) {
    const val = (raw as Record<number, unknown>)[key];
    if (val !== undefined && val !== null) {
      result[key] = normalizeIdealWeekEntry(val);
    }
  }
  return result;
}

const CACHE_KEY_PREFIX = 'orlo_ideal_week_';

function getCacheKey(userId: string): string {
  return `${CACHE_KEY_PREFIX}${userId}`;
}

function readCache(userId: string): IdealWeek | undefined {
  try {
    const cached = localStorage.getItem(getCacheKey(userId));
    if (cached) {
      return normalizeIdealWeek(JSON.parse(cached));
    }
  } catch {
    // 快取讀取失敗，忽略
  }
  return undefined;
}

function writeCache(userId: string, data: IdealWeek): void {
  try {
    localStorage.setItem(getCacheKey(userId), JSON.stringify(data));
  } catch {
    // 快取寫入失敗，忽略
  }
}

interface UseIdealWeekReturn {
  idealWeek: IdealWeek | undefined;
  isLoading: boolean;
  updateIdealWeek: (data: IdealWeek) => Promise<boolean>;
}

export function useIdealWeek(userId: string | null): UseIdealWeekReturn {
  const cachedValue = userId ? readCache(userId) : undefined;
  const [idealWeek, setIdealWeek] = useState<IdealWeek | undefined>(cachedValue);
  const [isLoading, setIsLoading] = useState(!cachedValue);

  useEffect(() => {
    if (!userId) {
      setIdealWeek(undefined);
      return;
    }

    setIsLoading(true);
    getUserData(userId)
      .then((data) => {
        const firestoreIdealWeek = data?.idealWeek
          ? normalizeIdealWeek(data.idealWeek)
          : undefined;
        if (firestoreIdealWeek) {
          setIdealWeek(firestoreIdealWeek);
          writeCache(userId, firestoreIdealWeek);
        } else {
          setIdealWeek(undefined);
        }
      })
      .catch(() => {
        // Firestore 載入失敗，保留快取資料
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [userId]);

  const updateIdealWeekFn = useCallback(async (data: IdealWeek): Promise<boolean> => {
    if (!userId) return false;

    const previousData = idealWeek;
    setIdealWeek(data);
    writeCache(userId, data);

    try {
      await updateUserIdealWeek(userId, data);
      return true;
    } catch {
      setIdealWeek(previousData);
      if (previousData) {
        writeCache(userId, previousData);
      } else {
        localStorage.removeItem(getCacheKey(userId));
      }
      throw new Error('Failed to update Ideal Week');
    }
  }, [userId, idealWeek]);

  return {
    idealWeek,
    isLoading,
    updateIdealWeek: updateIdealWeekFn,
  };
}
